import { Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {EAccountConfirmationStatus} from '@app/auth-config/models/auth-state.model';
import {IForgotPasswordForm} from '@app/auth-config/models/forgot-password-form.model';
import {forgotPassword} from '@app/auth-config/state/auth.actions';
import {selectForgotPasswordStatus} from '@app/auth-config/state/auth.selector';
import {Email_Regex} from '@app/shared/constants';
import {Store} from '@ngrx/store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {ToastMessageOptions} from 'primeng/api';
import {Subject, takeUntil, tap} from 'rxjs';
import { LoginInfoComponent } from '../../../shared/components/login-info/login-info.component';
import { Bind } from 'primeng/bind';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Button } from 'primeng/button';
import { LanguageSelectorComponent } from '../../../shared/components/language-selector/language-selector.component';

@Component({
    selector: 'app-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss'],
    imports: [
        LoginInfoComponent,
        FormsModule,
        ReactiveFormsModule,
        Bind,
        InputText,
        Message,
        Button,
        LanguageSelectorComponent,
        TranslatePipe,
    ],
})
export class ForgotPasswordComponent implements OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);

  forgotPasswordForm: FormGroup<IForgotPasswordForm>;
  messages: ToastMessageOptions[] = [];
  private readonly unsubscribe$ = new Subject<void>();
  submitInProgress = false;

  constructor() {
    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(Email_Regex)]],
    });

    this.store
      .select(selectForgotPasswordStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((status: EAccountConfirmationStatus) => {
          if (status === EAccountConfirmationStatus.Confirmed || status === EAccountConfirmationStatus.Error) {
            this.submitInProgress = false;
          }
        })
      )
      .subscribe();

    this.getForgotPasswordMessage();
  }

  private getForgotPasswordMessage() {
    this.translate.get(['Common.Info', 'Auth.ForgotPasswordInfo']).subscribe(() => {
      this.messages = [
        {
          severity: 'info',
          summary: this.translate.instant('Common.Info'),
          detail: this.translate.instant('Auth.ForgotPasswordInfo'),
          icon: 'pi pi-info-circle',
        },
      ];
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSubmit() {
    if (this.forgotPasswordForm?.valid) {
      this.submitInProgress = true;
      this.store.dispatch(
        forgotPassword({
          email: this.forgotPasswordForm.get('email')?.value ?? '',
        })
      );
    }
  }
}
